/* ---------------------------------------------------------------------------
   Measure it on the photograph.

   `QC-08`, and it is an ASSIST — the answer given when asked whether automatic
   detection should be the input or a help. Nothing here detects anything. The
   inspector marks something of known size in the frame, traces the defect, and
   the arithmetic in src/data/photoScale.ts turns pixels into millimetres.

   WHY NOT AUTOMATIC SEGMENTATION. Detecting that a photograph contains a crack
   is a solved problem and would run on the device. Measuring one is not the same
   job: no photograph carries absolute scale, so something of known size has to be
   in the frame either way — which means the inspector is marking a reference
   whether a model traces the crack or they do. What a model would save is the
   tracing, and what it would cost is several megabytes of weights downloaded
   over a district connection and a number nobody watched being produced. The
   tracing is the cheap half. The scale is the whole problem.

   THE REFUSAL IS THE FEATURE. `resolutionVerdict` decides whether the
   photograph can resolve what is being measured, and this dialog will not hand
   back a crack width from an image that cannot — with the arithmetic on screen,
   because "the photo isn't good enough" without a number is advice nobody can
   act on. A 2 m length off the same photograph is fine and is offered.

   TWO STEPS, IN ORDER, AND THE ORDER IS NOT NEGOTIABLE. Scale first, then trace.
   Tracing first and scaling afterwards works arithmetically and invites somebody
   to trace, get a plausible-looking pixel count, and accept it — which is the
   failure this whole dialog exists to prevent.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type MeasureState, type State } from '../core/store.ts';
import { button, field, scrim, seg } from '../components/widgets.ts';
import { trimNumber } from '../data/quantities.ts';
import {
  CRITICAL_WIDTH_MM,
  mmPerPixel,
  resolutionVerdict,
  scaleSourceNote,
  type PhotoScale,
} from '../data/photoScale.ts';
import {
  scaleOf,
  traceableFields,
  traceRefusal,
  traceResult,
  type TraceResult,
} from '../data/photoTrace.ts';

/** The dialog, or null when it is closed. */
export function measureDialog(s: Readonly<State>): HTMLElement | null {
  const m = s.measure;
  if (!m) return null;

  const scale = scaleOf(m);
  const result = traceResult(m);

  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeMeasure(), () => false),
    },
    h(
      'div',
      {
        class: 'dlg__panel dlg__panel--measure',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'Measure on the photograph',
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: 'assist · nothing is stored until you accept it' }),
          h('h2', { class: 'dlg__title', text: 'Measure on the photograph' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Cancel',
            'aria-label': 'Cancel',
            onclick: () => actions.closeMeasure(),
          },
          '×',
        ),
      ),

      h(
        'div',
        { class: 'dlg__body' },
        steps(m, scale !== null),
        canvas(m),
        m.phase === 'scale' ? scaleControls(m, scale) : traceControls(m, result),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: () => actions.closeMeasure() }),
        m.phase === 'scale'
          ? button('Next — trace the issue', {
              primary: true,
              disabled: scale === null,
              onClick: () => actions.measurePhase('trace'),
            })
          : button(
              result ? `Use ${trimNumber(Number(result.value.toFixed(4)))} ${result.field.unit}` : 'Use this measurement',
              {
                primary: true,
                disabled: result === null,
                onClick: () => actions.acceptMeasurement(),
              },
            ),
      ),
    ),
  );
}

/* --------------------------- the two steps --------------------------- */

function steps(m: MeasureState, scaled: boolean): HTMLElement {
  return h(
    'div',
    { class: 'msr__steps' },
    step(1, 'Set the scale', m.phase === 'scale', scaled),
    step(2, 'Trace the issue', m.phase === 'trace', false),
  );
}

function step(n: number, label: string, on: boolean, done: boolean): HTMLElement {
  return h(
    'span',
    { class: ['msr__step', on && 'is-on', done && 'is-done'] },
    h('span', { class: 'msr__step-n', text: done ? '✓' : String(n) }),
    h('span', { text: label }),
  );
}

/**
 * The photograph, with the marks on it.
 *
 * An `<img>` under an inline `<svg>` rather than a `<canvas>`, for two reasons
 * that both matter here. The marks stay crisp at any zoom because they are
 * vectors rather than pixels drawn once at one size; and the whole thing renders
 * from state on every paint, which is what lets undo be "drop the last point"
 * rather than "redraw everything except the last point".
 *
 * The click handler converts a viewport coordinate into a normalised one against
 * the element's own box. `getBoundingClientRect` is what makes it correct under
 * the dialog's scroll and any CSS scaling — the natural pixel dimensions live on
 * the state and are used only by the arithmetic.
 */
function canvas(m: MeasureState): HTMLElement {
  const marks = m.phase === 'scale' ? m.scalePoints : m.points;

  return h(
    'div',
    {
      class: 'msr__canvas',
      onclick: (e: Event) => {
        const ev = e as MouseEvent;
        const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
        if (box.width <= 0 || box.height <= 0) return;
        actions.addMeasurePoint({
          x: (ev.clientX - box.left) / box.width,
          y: (ev.clientY - box.top) / box.height,
        });
      },
    },
    h('img', {
      class: 'msr__img',
      src: m.photo,
      alt: 'The issue photograph, for measuring on',
      draggable: 'false',
    }),
    overlay(m, marks),
  );
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * One SVG element with attributes and children.
 *
 * `h()` builds HTML elements, and an `<svg>` created in the HTML namespace
 * renders as nothing at all — silently, which is why this is a helper rather
 * than a cast. `icon()` in src/core/dom.ts solves the same problem the same way
 * for its fixed shape; this is the general case, kept local because nothing else
 * in the application draws vectors from data.
 */
function svg(tag: string, attrs: Record<string, string>, ...children: Element[]): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  for (const child of children) el.appendChild(child);
  return el as SVGElement;
}

/**
 * The marks, over the photograph.
 *
 * A `viewBox` of `0 0 100 100` with `preserveAspectRatio: none`, so a normalised
 * point is drawn by multiplying by 100 and the overlay stretches with the image
 * whatever its aspect ratio. The arithmetic that MATTERS never uses this frame —
 * it uses the natural pixel dimensions, because a normalised distance is
 * aspect-distorted and this deliberately is too.
 */
function overlay(m: MeasureState, marks: readonly { x: number; y: number }[]): SVGElement {
  const children: Element[] = [];

  /* A polyline while tracing a length or a width, a closed polygon once there
     are three points and the target is an area — so the shape on screen is the
     shape the arithmetic is about. */
  if (marks.length > 1) {
    const closed = m.phase === 'trace' && m.target === 'areaM2' && marks.length > 2;
    children.push(
      svg(closed ? 'polygon' : 'polyline', {
        class: 'msr__path',
        points: marks.map((p) => `${p.x * 100},${p.y * 100}`).join(' '),
      }),
    );
  }

  marks.forEach((p, i) => {
    children.push(
      svg('circle', {
        class: 'msr__dot',
        cx: String(p.x * 100),
        cy: String(p.y * 100),
        r: '1.2',
      }),
    );
    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('class', 'msr__dot-n');
    label.setAttribute('x', String(p.x * 100 + 2));
    label.setAttribute('y', String(p.y * 100 - 1.6));
    label.textContent = String(i + 1);
    children.push(label);
  });

  return svg(
    'svg',
    {
      class: 'msr__overlay',
      viewBox: '0 0 100 100',
      preserveAspectRatio: 'none',
      'aria-hidden': 'true',
    },
    ...children,
  );
}

function scaleControls(m: MeasureState, scale: PhotoScale | null): HTMLElement {
  const perPx = scale ? mmPerPixel(scale) : null;

  return h(
    'div',
    { class: 'msr__panel' },
    h('div', {
      class: 'dlg__hint',
      text:
        m.scalePoints.length < 2
          ? 'Tap the two ends of something whose size you know.'
          : 'Now say how long that is, in millimetres.',
    }),
    h(
      'div',
      { class: 'msr__row' },
      field(
        'Reference length (mm)',
        h('input', {
          id: 'msr-ref',
          class: 'input msr__num',
          type: 'number',
          step: 'any',
          min: '0',
          placeholder: '—',
          disabled: m.scalePoints.length < 2,
          value: m.referenceMm,
          oninput: (e: Event) => actions.setMeasureReference((e.target as HTMLInputElement).value),
        }),
        'msr-ref',
      ),
      button('Clear marks', { sm: true, onClick: () => actions.clearMeasurePoints() }),
    ),
    perPx === null
      ? null
      : h(
          'div',
          { class: 'msr__verdict' },
          h('div', {
            text: `This photograph resolves about ${trimNumber(Number(perPx.toFixed(4)))} mm per pixel at ${m.width} × ${m.height}.`,
          }),
          /* What that means for the measurement DoR's rating bands turn on,
             stated before the inspector spends time tracing. */
          widthVerdict(scale!),
        ),
  );
}

/** Whether this photograph could report a crack width at all, and why not. */
function widthVerdict(scale: PhotoScale): HTMLElement | null {
  const v = resolutionVerdict(scale, CRITICAL_WIDTH_MM);
  if (!v) return null;
  return h('div', {
    class: ['msr__res', !v.ok && 'msr__res--no'],
    text: v.ok
      ? `Fine enough for a crack width: ${v.reason}`
      : `Not fine enough for a crack width. ${v.reason} Lengths and areas are still measurable.`,
  });
}

function traceControls(m: MeasureState, result: TraceResult | null): HTMLElement {
  const options = traceableFields(m.code);
  const scale = scaleOf(m);

  return h(
    'div',
    { class: 'msr__panel' },
    options.length
      ? h(
          'div',
          { class: 'msr__row' },
          h('span', { class: 'msr__cap', text: 'Measuring' }),
          seg(
            options.map((f) => ({
              label: f.label,
              on: m.target === f.key,
              onClick: () => actions.setMeasureTarget(f.key),
            })),
          ),
        )
      : h('div', { class: 'dlg__hint', text: 'This category takes no measurement a photograph can supply.' }),

    h('div', {
      class: 'dlg__hint',
      text:
        !m.target && options.length > 1
          ? 'Choose what you are measuring, then tap.'
          : m.target === 'areaM2'
          ? 'Tap around the edge of the affected area.'
          : m.target === 'widthMm'
            ? 'Tap the two sides of the crack at its widest point.'
            : 'Tap along the issue.',
    }),

    h(
      'div',
      { class: 'msr__row' },
      button('Undo last point', { sm: true, onClick: () => actions.undoMeasurePoint() }),
      button('Clear', { sm: true, onClick: () => actions.clearMeasurePoints() }),
      h('span', { class: 'spacer' }),
      button('Back to scale', { sm: true, onClick: () => actions.measurePhase('scale') }),
    ),

    result
      ? h(
          'div',
          { class: 'msr__result' },
          h('strong', {
            text: `${trimNumber(Number(result.value.toFixed(4)))} ${result.field.unit}`,
          }),
          h('span', { text: ` — ${result.field.label}, ` }),
          h('span', { text: scale ? scaleSourceNote(scale) : '' }),
        )
      : h('div', { class: 'msr__result msr__result--none', text: traceRefusal(m) }),
  );
}

